<script setup lang="ts">
import { type PropType, computed } from '@mimir/web/compat/vueCompat';
import { getCurrentInstance } from 'vue';
import { VBtn } from 'vuetify/lib';
import MIcon from '@mimir/web/compat/components/m-icon/MIcon.vue';
import type { IconName } from '@mimir/web/compat/components/m-icon/MIcon.types';
import { makeBtnVariantProps } from '@mimir/web/compat/components/props';
import type { NamedSize } from '@mimir/web/compat/components/props';
import { toV2BtnVariant, toV2Size } from '@mimir/web/compat/components/vuetify2Conversions';

const props = defineProps({
  icon: {
    type: String as PropType<IconName>,
    default: ''
  },
  defaultSlotIcon: {
    type: Boolean,
    default: false,
  },
  color: {
    type: String,
    default: ''
  },
  size: { type: String as PropType<NamedSize> },
  disabled: {
    type: Boolean,
    default: false,
  },
  ...makeBtnVariantProps(),
});

const size = computed(() => props.size ?? 'default');

const vm = getCurrentInstance()!.proxy as Vue;
const slotHasMIcon = computed(() => vm.$slots['default']?.[0]?.tag?.includes('MIcon'));

// Bound after the consumer's attrs, so only the presence of an icon decides the text style.
const isTextButton = computed(() => !props.icon && !slotHasMIcon.value && !props.defaultSlotIcon);

const vuetify2Props = computed(() => ({
  ...toV2Size(size.value),
  icon: !!props.icon || slotHasMIcon.value || props.defaultSlotIcon,
  disabled: props.disabled,
  ...toV2BtnVariant(props.variant ?? 'elevated'),
}));

</script>


<template>
  <VBtn
    v-bind="{ ...vuetify2Props, ...$attrs, text: isTextButton }"
    v-on="$listeners"
  >
    <MIcon
      v-if="icon"
      :icon="icon"
      :size="size"
      :color="color"
    />
    <slot />
  </VBtn>
</template>

<style scoped lang="stylus">
.v-btn--text
  :deep(.v-btn__content)
    letter-spacing: 0
.v-btn--disabled :deep(.custom-icon)
  opacity 0.3 !important
</style>
